import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Plus,
    Eye,
    Edit2,
    Trash2,
    Search,
    Filter,
    X,
    AlertCircle,
    Minus,
    Clock,
    BookOpen,
    ChevronRight,
    Building,
    User
} from "lucide-react";
import "./MasterList.css";
import {
    fetchBranchList, fetchCourseList, fetchBranchCourseList, fetchBatchList,
    createBranch, createCourse, mapBranchCourse, createBatches,
    updateBranch, deleteBranch, updateCourse, deleteCourse, updateBatch, deleteBatch,
    getPincodeDetails, courseAction, checkCourseStudents, getCourseStudentDetails, deleteCourseBatch
} from "../../services/masterService";

const convertTo24Hour = (time12h) => {
    if (!time12h) return "";
    const parts = time12h.trim().split(/\s+/);
    if (parts.length < 2) return parts[0] || "";
    const time = parts[0];
    const modifier = parts[1].toUpperCase();
    let [hours, minutes] = time.split(':');
    if (hours === '12') hours = '00';
    if (modifier === 'PM') hours = parseInt(hours, 10) + 12;
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
};

const convertTo12Hour = (time24h) => {
    if (!time24h) return "";
    let [hours, minutes] = time24h.split(':');
    if (!minutes) return time24h;
    let h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12;
    const hStr = h < 10 ? '0' + h : h;
    return `${hStr}:${minutes} ${ampm}`;
};

const MasterList = () => {
    const [activeType, setActiveType] = useState("Branch");
    const [searchTerm, setSearchTerm] = useState("");
    const [showViewModal, setShowViewModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "success" });
    const [modalContext, setModalContext] = useState(""); // Tracks what the modal is currently handling
    const [errors, setErrors] = useState({});
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState("");

    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [mappingBranch, setMappingBranch] = useState(null);
    const [selectedCourseToMap, setSelectedCourseToMap] = useState("");
    const [mappedCourses, setMappedCourses] = useState([]);
    const [showMappingDropdown, setShowMappingDropdown] = useState(false);

    // Unmap Flow States
    const [showUnmapCheckModal, setShowUnmapCheckModal] = useState(false);
    const [unmapCheckData, setUnmapCheckData] = useState(null);
    const [showUnmapDetailsModal, setShowUnmapDetailsModal] = useState(false);
    const [unmapStudentDetails, setUnmapStudentDetails] = useState([]);
    const [courseToUnmap, setCourseToUnmap] = useState(null);

    const navigate = useNavigate();

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Form State
    const initialBranchForm = {
        BranchName: "",
        branchCode: "",
        street: "",
        area: "",
        city: "",
        state: "",
        country: "",
        postalCode: "",
        locationLatitude: "",
        locationLongitude: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        bankName: "",
        bankBranchName: "",
        ifsc: "",
        accountNumber: "",
        accountType: "",
        accountHolderName: "",
        currencyCode: "",
        panNo: "",
        msme: "",
        upi: "",
        gstNo: ""
    };

    const initialCourseForm = {
        CourseNames: [""]
    };

    const initialBatchForm = {
        branchCourseId: "",
        selectedCourseName: "",
        batches: [
            { batchName: "", timeRange: "" }
        ]
    };

    const [formData, setFormData] = useState(initialBranchForm);
    const [branches, setBranches] = useState([]);

    const [courses, setCourses] = useState([]);
    const [branchCourseList, setBranchCourseList] = useState([]);
    const [batches, setBatches] = useState([]);

    useEffect(() => {
        loadData(activeType);
    }, [activeType]);

    // Reset pagination when switching tabs or searching
    useEffect(() => {
        setCurrentPage(1);
    }, [activeType, searchTerm]);

    // Auto-clear validation errors as the user types
    useEffect(() => {
        const fieldNames = Object.keys(errors);
        if (fieldNames.length === 0) return;

        setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            let hasChanges = false;

            fieldNames.forEach(field => {
                // Check both camelCase and PascalCase (common in this codebase)
                const val = formData[field] || formData[field.charAt(0).toUpperCase() + field.slice(1)];

                // If the user has entered a value, clear the error for this field
                if (val && val.toString().trim() !== "" && prevErrors[field]) {
                    delete newErrors[field];
                    hasChanges = true;
                }
            });

            return hasChanges ? newErrors : prevErrors;
        });
    }, [formData]);

    const getFilteredData = () => {
        let data = [];
        if (activeType === "Branch") data = branches;
        else if (activeType === "Course") data = courses;
        else if (activeType === "Batch") data = batches;

        if (searchTerm) {
            const lowSearch = searchTerm.toLowerCase();
            return data.filter(item => {
                const searchFields = [
                    item.BranchName, item.branchName,
                    item.BranchCode, item.branchCode,
                    item.CourseName, item.courseName,
                    item.BatchName, item.batchName,
                    item.BatchTime, item.batchTime,
                    item.City, item.city,
                    item.ContactName, item.contactName,
                    item.ContactPhone, item.contactPhone,
                    item.ContactEmail, item.contactEmail
                ];
                return searchFields
                    .filter(Boolean)
                    .some(field => String(field).toLowerCase().includes(lowSearch));
            });
        }
        return data;
    };

    const filteredData = getFilteredData();
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const loadData = async (type = activeType) => {
        try {
            setIsLoading(true);
            if (type === "Branch") {
                const res = await fetchBranchList();
                setBranches(res.data || []);
            } else if (type === "Course") {
                const res = await fetchCourseList();
                setCourses(res.data || []);
            } else if (type === "Batch") {
                const [batchRes, bcRes] = await Promise.all([
                    fetchBatchList(),
                    fetchBranchCourseList() // Needed for the mapping dropdown in Add Batch
                ]);
                console.log("Raw Batches Data:", batchRes.data);
                setBatches(batchRes.data || []);
                setBranchCourseList(bcRes.data || []);
            }
        } catch (err) {
            console.error("Failed to load data", err);
            showPopup("Error", typeof err === "string" && err ? err : "Could not load master data", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const showPopup = (title, message, type = "success") => {
        setPopup({ show: true, title, message, type });
    };

    const handleAddClick = async () => {
        setIsEditMode(false);
        setIsViewMode(false);
        setModalContext(activeType);

        // Lazy load dependencies for specific modals
        if (activeType === "Batch" && branchCourseList.length === 0) {
            try {
                setIsLoading(true);
                const bcRes = await fetchBranchCourseList();
                setBranchCourseList(bcRes.data || []);
            } finally {
                setIsLoading(false);
            }
        }

        if (activeType === "Branch") setFormData(initialBranchForm);
        else if (activeType === "Course") setFormData(initialCourseForm);
        else setFormData(initialBatchForm);
        setErrors({});
        setShowAddModal(true);
    };

    const handleCourseNameChange = (index, value) => {
        const newNames = [...formData.CourseNames];
        newNames[index] = value;
        setFormData(prev => ({ ...prev, CourseNames: newNames }));
    };

    const addCourseField = () => {
        setFormData(prev => ({ ...prev, CourseNames: [...prev.CourseNames, ""] }));
    };

    const removeCourseField = (index) => {
        if (formData.CourseNames.length <= 1) return;
        const newNames = [...formData.CourseNames];
        newNames.splice(index, 1);
        setFormData(prev => ({ ...prev, CourseNames: newNames }));
    };

    const handleBatchChange = (index, field, value) => {
        const newBatches = [...formData.batches];
        newBatches[index][field] = value;

        // Auto-calculate session if time is changed
        if (field === "batchTime" && value) {
            const hour = parseInt(value.split(":")[0]);
            let session = "Morning";
            if (hour >= 12 && hour < 16) session = "Afternoon";
            else if (hour >= 16 || hour < 5) session = "Evening";
            newBatches[index].batchName = session;
        }

        setFormData(prev => ({ ...prev, batches: newBatches }));
    };

    const addBatchField = () => {
        setFormData(prev => ({
            ...prev,
            batches: [...prev.batches, { batchName: "", batchTime: "" }]
        }));
    };

    const removeBatchField = (index) => {
        if (formData.batches.length <= 1) return;
        const newBatches = [...formData.batches];
        newBatches.splice(index, 1);
        setFormData(prev => ({ ...prev, batches: newBatches }));
    };

    const handleEditClick = (item) => {
        setIsEditMode(true);
        setIsViewMode(false);
        setModalContext(activeType);
        setSelectedItem(item);

        if (activeType === "Branch") {
            // Map API response to form data
            setFormData({
                BranchName: item.BranchName || item.branchName || "",
                branchCode: item.BranchCode || item.branchCode || "",
                street: item.Street || item.street || "",
                area: item.Area || item.area || "",
                city: item.City || item.city || "",
                state: item.State || item.state || "",
                country: item.Country || item.country || "",
                postalCode: item.PostalCode || item.postalCode || "",
                locationLatitude: item.LocationLatitude || item.locationLatitude || "",
                locationLongitude: item.LocationLongitude || item.locationLongitude || "",
                contactName: item.ContactName || item.contactName || "",
                contactEmail: item.ContactEmail || item.contactEmail || "",
                contactPhone: item.ContactPhone || item.contactPhone || "",
                bankName: item.BankName || item.bankName || "",
                bankBranchName: item.BankBranchName || item.bankBranchName || "",
                ifsc: item.IFSC || item.ifsc || "",
                accountNumber: item.AccountNumber || item.accountNumber || "",
                accountType: item.AccountType || item.accountType || "",
                accountHolderName: item.AccountHolderName || item.accountHolderName || "",
                currencyCode: item.CurrencyCode || item.currencyCode || "",
                panNo: item.PanNo || item.panNo || "",
                gstNo: item.GstNo || item.gstNo || "",
                msme: item.MSME || item.msme || "",
                upi: item.UPI || item.upi || "",
                BranchId: item.BranchId || item.id
            });
        } else if (activeType === "Course") {
            setFormData({
                CourseNames: [item.CourseName || item.courseName || ""],
                CourseId: item.CourseId || item.id
            });
        } else if (activeType === "Batch") {
            setFormData({
                branchCourseId: item.BranchCourseId || item.branchCourseId || "",
                batches: [{
                    batchName: item.BatchName || item.batchName || "",
                    timeRange: item.BatchTime ? convertTo24Hour(item.BatchTime) : ""
                }],
                BatchId: item.BatchId || item.id
            });
        }
        setErrors({});
        setShowAddModal(true);
    };

    const handleQuickCourseAdd = (branchId) => {
        setIsEditMode(false);
        setModalContext("Course");
        setFormData({ CourseNames: [""] }); // We are moving mapping to a separate step
        setShowAddModal(true);
    };

    const validateBranch = () => {
        const newErrors = {};
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{10}$/;
        const postalRegex = /^[0-9]{6}$/;
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

        if (!formData.BranchName) newErrors.BranchName = "Branch Name is required";
        if (!formData.street && !formData.Street) newErrors.street = "Street is required";
        if (!formData.area && !formData.Area) newErrors.area = "Area is required";
        if (!formData.city && !formData.City) newErrors.city = "City is required";
        if (!formData.state && !formData.State) newErrors.state = "State is required";
        if (!formData.country && !formData.Country) newErrors.country = "Country is required";

        const pCode = formData.postalCode || formData.PostalCode;
        if (!pCode) newErrors.postalCode = "Postal Code is required";
        else if (!postalRegex.test(pCode)) newErrors.postalCode = "Invalid Postal Code (6 digits)";

        if (!formData.contactName && !formData.ContactName) newErrors.contactName = "Contact Name is required";

        const email = formData.contactEmail || formData.ContactEmail;
        if (!email) newErrors.contactEmail = "Email is required";
        else if (!emailRegex.test(email)) newErrors.contactEmail = "Invalid Email format";

        const phone = formData.contactPhone || formData.ContactPhone;
        if (!phone) newErrors.contactPhone = "Phone is required";
        else if (!phoneRegex.test(phone)) newErrors.contactPhone = "Invalid Phone (10 digits)";

        // Banking & Tax (Optional except PAN and GST)
        const ifsc = formData.ifsc || formData.IFSC;
        if (ifsc && !ifscRegex.test(ifsc.toUpperCase())) {
            newErrors.ifsc = "Invalid IFSC format (e.g. ABCD0123456)";
        }

        const pan = formData.panNo || formData.PanNo;
        if (!pan) newErrors.panNo = "PAN Number is required";
        else if (!panRegex.test(pan.toUpperCase())) newErrors.panNo = "Invalid PAN format (e.g. ABCDE1234F)";

        const gst = formData.gstNo || formData.GstNo;
        if (gst && !gstRegex.test(gst.toUpperCase())) newErrors.gstNo = "Invalid GST format (15 digits)";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveBranch = async () => {
        if (!validateBranch()) {
            showPopup("Validation Error", "Please fix the errors before saving", "error");
            return;
        }

        const payload = {
            BranchId: isEditMode ? (formData.BranchId || selectedItem?.BranchId || selectedItem?.id) : undefined,
            BranchName: formData.BranchName,
            BranchCode: formData.branchCode,
            Street: formData.street,
            Area: formData.area,
            City: formData.city,
            State: formData.state,
            Country: formData.country,
            PostalCode: formData.postalCode,
            ContactName: formData.contactName,
            ContactEmail: formData.contactEmail,
            ContactPhone: formData.contactPhone,
            BankName: formData.bankName,
            BankBranchName: formData.bankBranchName,
            IFSC: formData.ifsc,
            AccountNumber: formData.accountNumber,
            AccountType: formData.accountType,
            AccountHolderName: formData.accountHolderName,
            CurrencyCode: formData.currencyCode,
            PanNo: formData.panNo,
            GstNo: formData.gstNo,
            MSME: formData.msme,
            UPI: formData.upi
        };

        try {
            setIsLoading(true);
            if (isEditMode) {
                await updateBranch(payload);
                showPopup("Success", "Branch updated successfully!", "success");
            } else {
                await createBranch(payload);
                showPopup("Success", "Branch created successfully!", "success");
            }
            loadData();
            setShowAddModal(false);
        } catch (err) {
            console.error("Branch Save failed", err);
            showPopup("Save Failed", typeof err === "string" ? err : "An error occurred while saving", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const validateCourse = () => {
        const newErrors = {};
        const emptyIndices = formData.CourseNames.map((name, i) => name.trim() === "" ? i : -1).filter(i => i !== -1);
        if (emptyIndices.length > 0) {
            newErrors.CourseNames = "All course names are mandatory";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveCourse = async () => {
        if (!validateCourse()) {
            showPopup("Validation Error", "Please enter all course names", "error");
            return;
        }

        const validNames = formData.CourseNames.filter(name => name.trim() !== "");

        try {
            setIsLoading(true);
            if (isEditMode) {
                const payload = {
                    courseName: validNames[0],
                    courseId: formData.CourseId || formData.id
                };
                await updateCourse(payload);
                showPopup("Success", "Course updated successfully!", "success");
            } else {
                await createCourse(validNames);
                showPopup("Success", `${validNames.length} Courses created successfully!`, "success");
            }
            loadData();
            setShowAddModal(false);
        } catch (err) {
            console.error("Course Save failed", err);
            showPopup("Save Failed", typeof err === "string" ? err : "An error occurred while saving", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const [mapFormData, setMapFormData] = useState({ branchId: "" });

    const handleMapClick = (course) => {
        setSelectedItem(course);
        setMapFormData({ branchId: "" });
        setShowMapModal(true);
    };

    const handleSaveMapping = async () => {
        if (!mapFormData.branchId) {
            showPopup("Missing Info", "Please select a branch", "error");
            return;
        }
        try {
            setIsLoading(true);
            await mapBranchCourse(mapFormData.branchId, [selectedItem.CourseId]);
            showPopup("Success", "Course mapped to branch successfully!", "success");
            loadData();
            setShowMapModal(false);
        } catch (err) {
            console.error("Mapping failed", err);
            showPopup("Error", typeof err === "string" && err ? err : "Could not map course", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const validateBatch = () => {
        const newErrors = {};
        if (!formData.branchCourseId) newErrors.branchCourseId = "Course-Branch mapping is required";

        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        formData.batches.forEach((b, idx) => {
            if (!b.batchName) newErrors[`batchName_${idx}`] = "Name required";
            if (!b.timeRange) {
                newErrors[`timeRange_${idx}`] = "Time required";
            } else if (!timeRegex.test(b.timeRange)) {
                newErrors[`timeRange_${idx}`] = "Format: 08:00";
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveBatch = async () => {
        if (!validateBatch()) {
            showPopup("Validation Error", "Please fill all required fields correctly", "error");
            return;
        }

        try {
            setIsLoading(true);
            const batchesPayload = formData.batches.map(b => ({
                batchName: b.batchName,
                batchTime: convertTo12Hour(b.timeRange)
            }));

            if (isEditMode) {
                const payload = {
                    batchId: formData.BatchId || formData.id,
                    batchName: batchesPayload[0].batchName,
                    batchTime: batchesPayload[0].batchTime,
                    branchCourseId: parseInt(formData.branchCourseId)
                };
                await updateBatch(payload);
                showPopup("Success", "Batch updated successfully!", "success");
            } else {
                await createBatches({
                    branchCourseId: parseInt(formData.branchCourseId),
                    batches: batchesPayload
                });
                showPopup("Success", "Batches created successfully!", "success");
            }
            loadData();
            setShowAddModal(false);
        } catch (err) {
            console.error("Batch save failed", err);
            showPopup("Error", typeof err === "string" && err ? err : "Could not save batch", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleView = (item) => {
        setSelectedItem(item);
        setIsViewMode(true);
        setIsEditMode(false);
        setModalContext(activeType);

        // Populate form data exactly like edit mode
        if (activeType === "Branch") {
            setFormData({
                BranchName: item.BranchName || item.branchName || "",
                branchCode: item.BranchCode || item.branchCode || "",
                street: item.Street || item.street || "",
                area: item.Area || item.area || "",
                city: item.City || item.city || "",
                state: item.State || item.state || "",
                country: item.Country || item.country || "",
                postalCode: item.PostalCode || item.postalCode || "",
                locationLatitude: item.LocationLatitude || item.locationLatitude || "",
                locationLongitude: item.LocationLongitude || item.locationLongitude || "",
                contactName: item.ContactName || item.contactName || "",
                contactEmail: item.ContactEmail || item.contactEmail || "",
                contactPhone: item.ContactPhone || item.contactPhone || "",
                bankName: item.BankName || item.bankName || "",
                bankBranchName: item.BankBranchName || item.bankBranchName || "",
                ifsc: item.IFSC || item.ifsc || "",
                accountNumber: item.AccountNumber || item.accountNumber || "",
                accountType: item.AccountType || item.accountType || "",
                accountHolderName: item.AccountHolderName || item.accountHolderName || "",
                currencyCode: item.CurrencyCode || item.currencyCode || "",
                panNo: item.PanNo || item.panNo || "",
                gstNo: item.GstNo || item.gstNo || "",
                msme: item.MSME || item.msme || "",
                upi: item.UPI || item.upi || "",
                BranchId: item.BranchId || item.id
            });
        } else if (activeType === "Course") {
            setFormData({
                CourseNames: [item.CourseName || item.courseName || ""],
                CourseId: item.CourseId || item.id
            });
        } else if (activeType === "Batch") {
            const courseMap = branchCourseList.flatMap(g => g.CourseList).find(c => String(c.BranchCourseId) === String(item.BranchCourseId || item.branchCourseId));
            setFormData({
                branchCourseId: item.BranchCourseId || "",
                selectedCourseName: courseMap ? courseMap.CourseName : (item.CourseName || ""),
                batches: [{
                    batchName: item.BatchName || "",
                    timeRange: item.BatchTime ? convertTo24Hour(item.BatchTime) : ""
                }],
                BatchId: item.BatchId
            });
        }

        setShowAddModal(true);
    };

    const handleDeleteClick = async (item) => {
        setSelectedItem(item);
        if (activeType === "Course") {
            try {
                setIsLoading(true);
                const id = item.CourseId || item.id;
                const response = await courseAction({ CourseId: id, Action: "CHECK" });
                const data = response.data || response;
                if (data && data.Message) {
                    setDeleteMessage(data.Message);
                } else {
                    setDeleteMessage(`Are you sure you want to delete this Course? This action cannot be undone.`);
                }
            } catch (err) {
                console.error("Check course failed", err);
                setDeleteMessage(`Are you sure you want to delete this Course? This action cannot be undone.`);
            } finally {
                setIsLoading(false);
                setShowDeleteModal(true);
            }
        } else {
            setDeleteMessage(`Are you sure you want to delete this ${activeType}? This action cannot be undone.`);
            setShowDeleteModal(true);
        }
    };

    const confirmDelete = async () => {
        try {
            setIsLoading(true);

            let id;
            if (activeType === "Branch") id = selectedItem.BranchId || selectedItem.id;
            else if (activeType === "Course") id = selectedItem.CourseId || selectedItem.id;
            else if (activeType === "Batch") id = selectedItem.BatchId || selectedItem.id;

            if (activeType === "Branch") await deleteBranch(id);
            else if (activeType === "Course") await courseAction({ CourseId: id, Action: "DELETE" });
            else if (activeType === "Batch") await deleteBatch(id);

            showPopup("Deleted", `${activeType} has been removed.`, "success");
            loadData();
            setShowDeleteModal(false);
            setSelectedItem(null);
        } catch (err) {
            showPopup("Delete Failed", typeof err === "string" ? err : "An error occurred during deletion", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenMapping = async (branch) => {
        setMappingBranch(branch);
        setShowMappingModal(true);
        try {
            setIsLoading(true);
            // Fetch both mapped list and full course list (for the dropdown)
            const [bcRes, cRes] = await Promise.all([
                fetchBranchCourseList(),
                fetchCourseList()
            ]);

            setCourses(cRes.data || []);

            const branchId = branch.BranchId || branch.id;
            // Find this specific branch's courses in the nested response
            const branchData = (bcRes.data || []).find(item => String(item.BranchId) === String(branchId));

            if (branchData && branchData.CourseList) {
                const mapped = branchData.CourseList.map(c => ({
                    ...c,
                    displayText: `${c.CourseName} - ${branchData.BranchName}`
                }));
                setMappedCourses(mapped);
            } else {
                setMappedCourses([]);
            }
        } catch (err) {
            console.error("Failed to load mapping data", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMapCourse = async () => {
        if (!selectedCourseToMap) {
            setPopup({ show: true, title: "Error", message: "Please select a course to map", type: "error" });
            return;
        }

        try {
            setIsLoading(true);
            const branchId = mappingBranch.BranchId || mappingBranch.id;
            await mapBranchCourse(branchId, selectedCourseToMap);

            setPopup({ show: true, title: "Success", message: "Course mapped successfully", type: "success" });
            setSelectedCourseToMap("");
            // Refresh mapped list
            const res = await fetchBranchCourseList();
            const branchData = (res.data || []).find(item => String(item.BranchId) === String(branchId));
            if (branchData && branchData.CourseList) {
                const mapped = branchData.CourseList.map(c => ({
                    ...c,
                    displayText: `${c.CourseName} - ${branchData.BranchName}`
                }));
                setMappedCourses(mapped);
            } else {
                setMappedCourses([]);
            }
        } catch (err) {
            setPopup({ show: true, title: "Error", message: typeof err === "string" && err ? err : "Failed to map course", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUnmapCourse = async (mappedCourse) => {
        try {
            setIsLoading(true);
            setCourseToUnmap(mappedCourse);
            const branchCourseId = mappedCourse.BranchCourseId;
            const res = await checkCourseStudents(branchCourseId);
            const data = res.data || res;
            setUnmapCheckData(data);
            setShowUnmapCheckModal(true);
        } catch (err) {
            setPopup({ show: true, title: "Error", message: typeof err === "string" && err ? err : "Failed to check course dependencies.", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewStudentDetails = async () => {
        if (!courseToUnmap) return;
        try {
            setIsLoading(true);
            const branchCourseId = courseToUnmap.BranchCourseId;
            const res = await getCourseStudentDetails(branchCourseId);
            const data = res.data || res;
            setUnmapStudentDetails(Array.isArray(data) ? data : []);
            setShowUnmapCheckModal(false);
            setShowUnmapDetailsModal(true);
        } catch (err) {
            setPopup({ show: true, title: "Error", message: typeof err === "string" && err ? err : "Failed to fetch student details.", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const confirmUnmapDelete = async () => {
        if (!courseToUnmap) return;
        try {
            setIsLoading(true);
            const branchCourseId = courseToUnmap.BranchCourseId;
            await deleteCourseBatch(branchCourseId);
            setPopup({ show: true, title: "Success", message: "Course mapping deleted successfully", type: "success" });
            
            // Refresh mapped list
            const branchId = mappingBranch.BranchId || mappingBranch.id;
            const res = await fetchBranchCourseList();
            const branchData = (res.data || []).find(item => String(item.BranchId) === String(branchId));
            if (branchData && branchData.CourseList) {
                const mapped = branchData.CourseList.map(c => ({
                    ...c,
                    displayText: `${c.CourseName} - ${branchData.BranchName}`
                }));
                setMappedCourses(mapped);
            } else {
                setMappedCourses([]);
            }

            setShowUnmapCheckModal(false);
            setShowUnmapDetailsModal(false);
            setCourseToUnmap(null);
        } catch (err) {
            setPopup({ show: true, title: "Error", message: typeof err === "string" && err ? err : "Failed to delete mapping.", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const handlePincodeChange = async (val) => {
        setFormData(prev => ({ ...prev, postalCode: val }));

        if (val.length === 6) {
            try {
                const data = await getPincodeDetails(val);
                if (data && data[0] && data[0].Status === "Success") {
                    const postOffice = data[0].PostOffice[0];
                    setFormData(prev => ({
                        ...prev,
                        city: postOffice.District,
                        state: postOffice.State,
                        country: "India",
                        area: postOffice.Name
                    }));
                    // Clear error if any
                    setErrors(prev => ({ ...prev, postalCode: "" }));
                }
            } catch (err) {
                console.error("Pincode fetch failed", err);
            }
        }
    };

    const checkIfDirty = () => {
        if (isViewMode) return false;

        let initial;
        if (modalContext === "Branch") {
            initial = isEditMode ? selectedItem : initialBranchForm;
        } else if (modalContext === "Course") {
            initial = isEditMode ? {
                CourseNames: [selectedItem.CourseName || selectedItem.courseName || ""],
                CourseId: selectedItem.CourseId || selectedItem.id
            } : initialCourseForm;
        } else if (modalContext === "Batch") {
            initial = isEditMode ? {
                selectedBranchId: selectedItem.BranchId || "",
                selectedCourseId: selectedItem.BranchCourseId || "",
                batches: [{
                    batchName: selectedItem.BatchName || "",
                    timeRange: selectedItem.BatchTime ? convertTo24Hour(selectedItem.BatchTime) : ""
                }],
                BatchId: selectedItem.BatchId
            } : initialBatchForm;
        }

        // Deep comparison of form data
        const currentStr = JSON.stringify(formData);
        const initialStr = JSON.stringify(initial);
        return currentStr !== initialStr;
    };

    const handleCloseModal = () => {
        if (checkIfDirty()) {
            setShowDiscardModal(true);
        } else {
            setShowAddModal(false);
        }
    };

    const confirmDiscard = () => {
        setShowDiscardModal(false);
        setShowAddModal(false);
    };

    const renderDeleteMessage = () => {
        const msg = deleteMessage || `Are you sure you want to delete this ${activeType}? This action cannot be undone.`;
        const regex = /(branches:\s*)(.*?)(\.\s*Delete)/i;
        const match = msg.match(regex);
        if (match) {
            const before = msg.substring(0, match.index + match[1].length);
            const branchName = match[2];
            const after = msg.substring(match.index + match[1].length + match[2].length);
            return (
                <span>
                    {before}
                    <strong style={{ color: "black", fontWeight: "bold" }}>{branchName}</strong>
                    {after}
                </span>
            );
        }
        return msg;
    };

    return (
        <div className="master-list-container">
            {/* HEADER SECTION */}
            <div className="master-header">
                <div className="header-left">
                    <div className="modern-type-selector" onClick={() => setShowTypeDropdown(!showTypeDropdown)}>
                        <Filter size={18} className="filter-icon" />
                        <div className="current-type">{activeType} List</div>
                        <ChevronRight size={16} className={`chevron-icon ${showTypeDropdown ? 'open' : ''}`} />

                        {showTypeDropdown && (
                            <div className="type-dropdown-menu">
                                <div
                                    className={`type-option ${activeType === "Branch" ? "active" : ""}`}
                                    onClick={() => { setActiveType("Branch"); setShowTypeDropdown(false); }}
                                >
                                    Branch List
                                </div>
                                <div
                                    className={`type-option ${activeType === "Course" ? "active" : ""}`}
                                    onClick={() => { setActiveType("Course"); setShowTypeDropdown(false); }}
                                >
                                    Course List
                                </div>
                                <div
                                    className={`type-option ${activeType === "Batch" ? "active" : ""}`}
                                    onClick={() => { setActiveType("Batch"); setShowTypeDropdown(false); }}
                                >
                                    Batch List
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="search-box">
                        <Search size={18} className="search-icon" />
                        <input
                            type="text"
                            placeholder={`Search ${activeType}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="header-right">
                    <button className="add-btn-premium" onClick={handleAddClick}>
                        <Plus size={20} />
                        <span>Add {activeType}</span>
                    </button>
                </div>
            </div>

            {/* GRID SECTION */}
            <div className="grid-wrapper">
                {isLoading && (
                    <div className="loader-overlay">
                        <div className="loader-spinner"></div>
                        <p>Processing...</p>
                    </div>
                )}
                <div className="table-scroll-area">
                    <table className={`master-table table-${activeType.toLowerCase()}`}>
                        <thead>
                            {activeType === "Branch" && (
                                <tr>
                                    <th className="text-center">S.No</th>
                                    <th>Branch Name</th>
                                    <th>Contact Person</th>
                                    <th>City</th>
                                    <th>Phone</th>
                                    <th>Course</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            )}
                            {activeType === "Course" && (
                                <tr>
                                    <th className="text-center">S.No</th>
                                    <th>Course Name</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            )}
                            {activeType === "Batch" && (
                                <tr>
                                    <th className="text-center">S.No</th>
                                    <th>Batch Name</th>
                                    <th>Time</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {activeType === "Branch" && paginatedData.map((item, index) => (
                                <tr key={item.BranchId || item.id}>
                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    <td>
                                        <div className="primary-cell">{item.BranchName}</div>
                                    </td>
                                    <td><span className="primary-cell">{item.ContactName}</span></td>
                                    <td>{item.City}</td>
                                    <td>{item.ContactPhone}</td>
                                    <td>
                                        <button
                                            className="course-mapping-btn"
                                            onClick={() => handleOpenMapping(item)}
                                        >
                                            <BookOpen size={14} /> Course Details
                                        </button>
                                    </td>
                                    <td>
                                        <div className="action-cell">
                                            <button className="action-btn view" onClick={() => handleView(item)} title="View Details">
                                                <Eye size={18} />
                                            </button>
                                            <button className="action-btn edit" onClick={() => handleEditClick(item)} title="Edit">
                                                <Edit2 size={18} />
                                            </button>
                                            <button className="action-btn delete" onClick={() => handleDeleteClick(item)} title="Delete">
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {activeType === "Course" && paginatedData.map((item, index) => (
                                <tr key={item.CourseId}>
                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    <td>
                                        <div className="primary-cell">{item.CourseName}</div>
                                    </td>
                                    <td>
                                        <div className="action-cell">
                                            <button className="action-btn edit" onClick={() => handleEditClick(item)} title="Edit">
                                                <Edit2 size={18} />
                                            </button>
                                            <button className="action-btn delete" onClick={() => handleDeleteClick(item)} title="Delete">
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {activeType === "Batch" && paginatedData.map((item, index) => (
                                <tr key={item.BatchId}>
                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    <td>
                                        <div className="primary-cell">{item.BatchName}</div>
                                    </td>
                                    <td>{item.BatchTime}</td>
                                    <td>
                                        <div className="action-cell">
                                            <button className="action-btn edit" onClick={() => handleEditClick(item)} title="Edit">
                                                <Edit2 size={18} />
                                            </button>
                                            <button className="action-btn delete" onClick={() => handleDeleteClick(item)} title="Delete">
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {paginatedData.length === 0 && !isLoading && (
                                <tr>
                                    <td colSpan="10" className="no-data-cell">
                                        <div className="no-data-content">
                                            <Filter size={40} />
                                            <p>No {activeType.toLowerCase()}s found matching your criteria</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION CONTROLS */}
                {filteredData.length > 0 && (
                    <div className="pagination-container">
                        <div className="pagination-info">
                            Showing <span>{filteredData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to <span>{Math.min(currentPage * itemsPerPage, filteredData.length)}</span> of <span>{filteredData.length}</span> entries
                        </div>
                        {totalPages > 1 && (
                            <div className="pagination-btns">
                                <button
                                    className="pag-btn"
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i + 1}
                                        className={`pag-num ${currentPage === i + 1 ? 'active' : ''}`}
                                        onClick={() => setCurrentPage(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    className="pag-btn"
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>



            {/* ADD MODAL */}
            {showAddModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className={`modal-card ${modalContext === "Batch" ? "ultra-wide" : (modalContext === "Branch" ? "ultra-wide" : "mini")}`} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{isViewMode ? "View" : isEditMode ? "Edit" : "Add New"} {modalContext}</h3>
                            <button className="close-btn" onClick={handleCloseModal}><X size={20} /></button>
                        </div>
                        <div className={`modal-body ${modalContext !== "Batch" ? "scrollable" : ""}`}>
                            <div className={isViewMode ? "view-only" : ""}>
                                {modalContext === "Branch" ? (
                                    <div className="add-form-layout">
                                        <div className="form-section">
                                            <h4>General Information</h4>
                                            <div className="form-grid-inner">
                                                <FormInput label="Branch Name" name="BranchName" isAlpha value={formData.BranchName} onChange={setFormData} error={errors.BranchName} readOnly={isViewMode} />
                                                <FormInput label="Contact Name" name="contactName" isAlpha value={formData.contactName} onChange={setFormData} error={errors.contactName} readOnly={isViewMode} />
                                                <FormInput label="Contact Email" name="contactEmail" value={formData.contactEmail} onChange={setFormData} error={errors.contactEmail} readOnly={isViewMode} />
                                                <FormInput label="Contact Phone" name="contactPhone" value={formData.contactPhone} onChange={setFormData} error={errors.contactPhone} isNumeric maxLength={10} readOnly={isViewMode} />

                                            </div>
                                        </div>
                                        <div className="form-section">
                                            <h4>Address Details</h4>
                                            <div className="form-grid-inner">
                                                <FormInput label="Postal Code" name="postalCode" value={formData.postalCode} onChange={(val) => typeof val === "function" ? handlePincodeChange(val({}).postalCode) : handlePincodeChange(val)} error={errors.postalCode} isNumeric maxLength={6} readOnly={isViewMode} />

                                                <FormInput label="Street" name="street" value={formData.street} onChange={setFormData} error={errors.street} readOnly={isViewMode} />
                                                <FormInput label="Area" name="area" value={formData.area} onChange={setFormData} error={errors.area} readOnly={isViewMode} />
                                                <FormInput label="City" name="city" isAlpha value={formData.city} onChange={setFormData} error={errors.city} readOnly={isViewMode} />
                                                <FormInput label="State" name="state" isAlpha value={formData.state} onChange={setFormData} error={errors.state} readOnly={isViewMode} />
                                                <FormInput label="Country" name="country" isAlpha value={formData.country} onChange={setFormData} error={errors.country} readOnly={isViewMode} />
                                            </div>
                                        </div>
                                        <div className="form-section">
                                            <h4>Banking & Tax</h4>
                                            <div className="form-grid-inner">
                                                <FormInput label="Account Holder Name" isAlpha name="accountHolderName" value={formData.accountHolderName} onChange={setFormData} error={errors.accountHolderName} readOnly={isViewMode} />
                                                <FormInput label="Bank Name" name="bankName" isAlpha value={formData.bankName} onChange={setFormData} error={errors.bankName} readOnly={isViewMode} />
                                                <FormInput label="Branch Name" name="bankBranchName" isAlpha value={formData.bankBranchName} onChange={setFormData} error={errors.bankBranchName} readOnly={isViewMode} />
                                                <FormInput label="IFSC Code" name="ifsc" value={formData.ifsc} onChange={setFormData} error={errors.ifsc} maxLength={11} readOnly={isViewMode} />
                                                <FormInput label="Account Number" name="accountNumber" value={formData.accountNumber} onChange={setFormData} error={errors.accountNumber} isNumeric readOnly={isViewMode} />
                                                <div className="form-input-group">
                                                    <label>Account Type</label>
                                                    <select
                                                        value={formData.accountType || ""}
                                                        onChange={(e) => !isViewMode && setFormData(prev => ({ ...prev, accountType: e.target.value }))}
                                                        disabled={isViewMode}
                                                        className={`form-select-modern ${errors.accountType ? "error-border" : ""} ${isViewMode ? "readonly-input" : ""}`}
                                                    >
                                                        <option value="">Select Account Type</option>
                                                        <option value="Savings">Savings</option>
                                                        <option value="Current Account">Current Account</option>
                                                    </select>
                                                    {errors.accountType && <span className="error-message-inline">{errors.accountType}</span>}
                                                </div>
                                                <FormInput label="Currency" name="currencyCode" isAlpha value={formData.currencyCode} onChange={setFormData} error={errors.currencyCode} maxLength={3} readOnly={isViewMode} />
                                                <FormInput label="PAN No" name="panNo" value={formData.panNo} onChange={setFormData} error={errors.panNo} maxLength={10} readOnly={isViewMode} />
                                                <FormInput label="GST No" name="gstNo" value={formData.gstNo} onChange={setFormData} error={errors.gstNo} maxLength={15} readOnly={isViewMode} />
                                                <FormInput label="MSME" name="msme" value={formData.msme} onChange={setFormData} error={errors.msme} readOnly={isViewMode} />
                                                <FormInput label="UPI ID" name="upi" value={formData.upi} onChange={setFormData} error={errors.upi} readOnly={isViewMode} />
                                            </div>
                                        </div>
                                    </div>
                                ) : modalContext === "Course" ? (
                                    <div className="add-form-simple">
                                        <label className="multi-label">Course Names</label>
                                        {formData.CourseNames.map((name, index) => (
                                            <div key={index} className="multi-input-row">
                                                <input
                                                    type="text"
                                                    value={name}
                                                    onChange={(e) => !isViewMode && handleCourseNameChange(index, e.target.value)}
                                                    placeholder={isViewMode ? "" : `Enter course name ${index + 1}`}
                                                    readOnly={isViewMode}
                                                    className={`form-input-modern ${errors.CourseNames ? "error-border" : ""} ${isViewMode ? "readonly-input" : ""}`}
                                                />
                                                {index === 0 && errors.CourseNames && <span className="error-message-inline">{errors.CourseNames}</span>}
                                                {!isViewMode && (
                                                    <div className="multi-actions">
                                                        {index === formData.CourseNames.length - 1 && (
                                                            <button className="multi-add-btn" onClick={addCourseField}>
                                                                <Plus size={16} />
                                                            </button>
                                                        )}
                                                        {formData.CourseNames.length > 1 && (
                                                            <button className="multi-remove-btn" onClick={() => removeCourseField(index)}>
                                                                <Minus size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="batch-form-container">
                                        <div className="section-header-modern">
                                            <span>COURSE & BRANCH</span>
                                        </div>
                                        <div className="course-branch-row">
                                            <div className="form-input-group flex-1">
                                                <label>Course</label>
                                                <div style={{ position: 'relative' }}>
                                                    <BookOpen className="select-icon-left" size={16} />
                                                    <select
                                                        className={`native-select-modern with-icon ${errors.selectedCourseName ? "error-border" : ""}`}
                                                        value={formData.selectedCourseName || ""}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, selectedCourseName: e.target.value, branchCourseId: "" }))}
                                                        disabled={isViewMode}
                                                    >
                                                        <option value="">Select Course</option>
                                                        {Array.from(new Set(branchCourseList.flatMap(g => g.CourseList.map(c => c.CourseName)))).map(cName => (
                                                            <option key={cName} value={cName}>{cName}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            
                                            <div className="form-input-group flex-1">
                                                <label>Branch</label>
                                                <div style={{ position: 'relative' }}>
                                                    <Building className="select-icon-left" size={16} />
                                                    <select
                                                        className={`native-select-modern with-icon ${errors.branchCourseId ? "error-border" : ""}`}
                                                        value={formData.branchCourseId || ""}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, branchCourseId: e.target.value }))}
                                                        disabled={!formData.selectedCourseName || isViewMode}
                                                    >
                                                        <option value="">Select Branch</option>
                                                        {branchCourseList.filter(g => g.CourseList.some(c => c.CourseName === formData.selectedCourseName)).map(g => {
                                                            const mappedCourse = g.CourseList.find(c => c.CourseName === formData.selectedCourseName);
                                                            return <option key={mappedCourse.BranchCourseId} value={mappedCourse.BranchCourseId}>{g.BranchName}</option>;
                                                        })}
                                                    </select>
                                                </div>
                                                {errors.branchCourseId && <span className="error-message-inline">{errors.branchCourseId}</span>}
                                            </div>
                                        </div>

                                        <div className="section-header-modern flex-between" style={{ marginTop: '16px' }}>
                                            <span>BATCH SCHEDULE</span>
                                            {!isViewMode && (
                                                <button className="add-batch-btn-outline" onClick={() => setFormData(prev => ({ ...prev, batches: [...prev.batches, { batchName: "", timeRange: "" }] }))}>
                                                    <Plus size={14} /> Add Batch
                                                </button>
                                            )}
                                        </div>

                                        <div className="batch-cards-container">
                                            {formData.batches.map((batch, index) => (
                                                <div key={index} className="batch-card-modern" style={index === 0 ? { borderLeft: '4px solid #16a34a' } : {}}>
                                                    <div className="batch-card-header">
                                                        <span className="batch-number-badge" style={index === 0 ? { background: '#dcfce7', color: '#16a34a' } : {}}>{index + 1}</span>
                                                        <span className="batch-card-title">{batch.batchName || `Batch ${index + 1}`}</span>
                                                        {!isViewMode && formData.batches.length > 1 && (
                                                            <button className="delete-batch-icon-btn" onClick={() => setFormData(prev => ({ ...prev, batches: prev.batches.filter((_, i) => i !== index) }))}>
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="batch-card-body">
                                                        <div className="form-input-group flex-1">
                                                            <label>Batch Name</label>
                                                            <div style={{ position: 'relative' }}>
                                                                <User className="select-icon-left" size={16} />
                                                                <input
                                                                    type="text"
                                                                    placeholder="e.g. Evening Batch"
                                                                    value={batch.batchName}
                                                                    onChange={(e) => !isViewMode && handleBatchChange(index, "batchName", e.target.value)}
                                                                    className={`form-input-modern with-icon ${errors[`batchName_${index}`] ? "error-border" : ""}`}
                                                                    readOnly={isViewMode}
                                                                />
                                                            </div>
                                                            {errors[`batchName_${index}`] && <span className="error-message-inline">{errors[`batchName_${index}`]}</span>}
                                                        </div>
                                                        
                                                        <div className="form-input-group flex-1">
                                                            <label>Start Time</label>
                                                            <div className="time-picker-modern">
                                                                <Clock size={16} className="time-icon-inside" />
                                                                <input
                                                                    type="time"
                                                                    value={batch.timeRange}
                                                                    onChange={(e) => !isViewMode && handleBatchChange(index, "timeRange", e.target.value)}
                                                                    className={`form-input-modern with-icon ${errors[`timeRange_${index}`] ? "error-border" : ""}`}
                                                                    readOnly={isViewMode}
                                                                    style={{ borderRight: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                                                />
                                                                <select
                                                                    className="period-select-modern"
                                                                    value={parseInt(batch.timeRange.split(":")[0]) >= 12 ? "PM" : "AM"}
                                                                    disabled={true}
                                                                    style={{ opacity: 0.8, cursor: 'default' }}
                                                                >
                                                                    <option value="AM">AM</option>
                                                                    <option value="PM">PM</option>
                                                                </select>
                                                            </div>
                                                            {errors[`timeRange_${index}`] && <span className="error-message-inline">{errors[`timeRange_${index}`]}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {!isViewMode && (
                                                <button className="add-another-batch-dotted" onClick={() => setFormData(prev => ({ ...prev, batches: [...prev.batches, { batchName: "", timeRange: "" }] }))}>
                                                    <Plus size={16} /> Add another batch
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={handleCloseModal}>{isViewMode ? "Close" : "Cancel"}</button>
                            {!isViewMode && (
                                <>
                                    {modalContext === "Branch" ? (
                                        <button className="save-btn-premium" onClick={handleSaveBranch}>
                                            {isEditMode ? "Update Branch" : "Save Branch"}
                                        </button>
                                    ) : modalContext === "Course" ? (
                                        <button className="save-btn-premium" onClick={handleSaveCourse}>
                                            {isEditMode ? "Update Course" : "Save Course"}
                                        </button>
                                    ) : (
                                        <button className="save-btn-premium" onClick={handleSaveBatch}>
                                            {isEditMode ? "Update Batch" : "Save Batch"}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* DISCARD CHANGES CONFIRMATION */}
            {showDiscardModal && (
                <div className="modal-overlay" onClick={() => setShowDiscardModal(false)}>
                    <div className="modal-card mini" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-confirm-body">
                            <div className="alert-icon-wrap">
                                <AlertCircle size={40} color="#f59e0b" />
                            </div>
                            <h4>Discard Changes?</h4>
                            <p>You have unsaved changes. Are you sure you want to close? All details entered will be removed.</p>
                            <div className="confirm-actions">
                                <button className="cancel-btn" onClick={() => setShowDiscardModal(false)}>Keep Editing</button>
                                <button className="delete-confirm-btn" style={{ background: '#f59e0b' }} onClick={confirmDiscard}>Discard</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-card mini" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-confirm-body">
                            <div className="alert-icon-wrap">
                                <AlertCircle size={40} color="#ef4444" />
                            </div>
                            <h4>Confirm Delete</h4>
                            <p>{renderDeleteMessage()}</p>
                            <div className="confirm-actions">
                                <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                                <button className="delete-confirm-btn" onClick={confirmDelete}>Delete</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* BRANCH MAP MODAL */}
            {showMapModal && selectedItem && (
                <div className="modal-overlay" onClick={() => setShowMapModal(false)}>
                    <div className="modal-card ultra-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Map {selectedItem.CourseName} to Branch</h3>
                            <button className="close-btn" onClick={() => setShowMapModal(false)}><X size={20} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-input-group">
                                <label>Select Branch</label>
                                <select
                                    value={mapFormData.branchId}
                                    onChange={(e) => setMapFormData({ branchId: e.target.value })}
                                    className="form-select-modern"
                                >
                                    <option value="">Choose a branch...</option>
                                    {branches.map(b => (
                                        <option key={b.BranchId} value={b.BranchId}>{b.BranchName}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setShowMapModal(false)}>Cancel</button>
                            <button className="save-btn-premium" onClick={handleSaveMapping} disabled={isLoading}>
                                {isLoading ? "Mapping..." : "Save Mapping"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* COURSE MAPPING MODAL */}
            {showMappingModal && (
                <div className="modal-overlay" onClick={() => setShowMappingModal(false)}>
                    <div className="modal-card ultra-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Course Mapping - {mappingBranch?.BranchName}</h3>
                            <button className="close-btn" onClick={() => setShowMappingModal(false)}><X size={20} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="mapping-container">
                                <div className="mapping-top">
                                    <div className="form-input-group">
                                        <label>Choose Course to Map</label>
                                        <select
                                            value={selectedCourseToMap}
                                            onChange={(e) => setSelectedCourseToMap(e.target.value)}
                                            className="form-select-modern"
                                        >
                                            <option value="">Select a Course</option>
                                            {courses.map(c => (
                                                <option key={c.CourseId} value={c.CourseId}>{c.CourseName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <button className="map-submit-btn" onClick={handleMapCourse}>
                                        Map Now
                                    </button>
                                </div>

                                <div className="mapped-bottom">
                                    <h4>Mapped Courses</h4>
                                    <div className="mapped-grid">
                                        {mappedCourses.length > 0 ? (
                                            mappedCourses.map((mc, idx) => (
                                                <div key={idx} className="mapped-course-card">
                                                    <div className="mapped-course-info">
                                                        <BookOpen size={14} />
                                                        <span>{mc.displayText}</span>
                                                    </div>
                                                    <button 
                                                        className="unmap-btn" 
                                                        onClick={() => handleUnmapCourse(mc)}
                                                        title="Remove Mapping"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="empty-mapping">
                                                <Filter size={24} />
                                                <p>No courses linked to this branch yet.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* UNMAP CHECK MODAL */}
            {showUnmapCheckModal && unmapCheckData && (
                <div className="modal-overlay" onClick={() => setShowUnmapCheckModal(false)}>
                    <div className="modal-card mini" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-confirm-body">
                            <div className="alert-icon-wrap">
                                <AlertCircle size={40} color="#f59e0b" />
                            </div>
                            <h4>Dependency Check</h4>
                            <p>{unmapCheckData.Message}</p>
                            <div className="confirm-actions">
                                <button className="cancel-btn" onClick={() => setShowUnmapCheckModal(false)}>Cancel</button>
                                <button className="save-btn-premium" onClick={handleViewStudentDetails}>View Details</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* UNMAP DETAILS MODAL */}
            {showUnmapDetailsModal && (
                <div className="modal-overlay" onClick={() => setShowUnmapDetailsModal(false)}>
                    <div className="modal-card wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Course Dependencies</h3>
                            <button className="close-btn" onClick={() => setShowUnmapDetailsModal(false)}><X size={20} /></button>
                        </div>
                        <div className="modal-body scrollable">
                            {unmapStudentDetails.length > 0 ? (
                                <div className="table-scroll-area" style={{ maxHeight: "300px", overflowX: "hidden" }}>
                                    <table className="master-table" style={{ minWidth: "100%" }}>
                                        <thead>
                                            <tr>
                                                <th className="text-center">Student ID</th>
                                                <th>Student Name</th>
                                                <th>Batch Name</th>
                                                <th>Batch Time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {unmapStudentDetails.map((student, idx) => (
                                                <tr key={idx}>
                                                    <td className="text-center">{student.StudentId}</td>
                                                    <td><div className="primary-cell">{student.StudentName}</div></td>
                                                    <td>{student.BatchName}</td>
                                                    <td>{student.BatchTime}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="no-data-cell">
                                    <div className="no-data-content">
                                        <Filter size={40} />
                                        <p>No students found for this mapped course. You can proceed with deletion.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setShowUnmapDetailsModal(false)}>Cancel</button>
                            <button className="delete-confirm-btn" onClick={confirmUnmapDelete}>Delete Mapping</button>
                            {unmapStudentDetails.length > 0 && (
                                <button className="save-btn-premium" onClick={() => {
                                    setShowUnmapDetailsModal(false);
                                    navigate("/dashboard/students");
                                }}>
                                    Transfer Students
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {popup.show && (
                <div className="popup-overlay" onClick={() => setPopup({ ...popup, show: false })}>
                    <div className="popup-card" onClick={(e) => e.stopPropagation()}>
                        <div className={`popup-icon ${popup.type}`}>
                            {popup.type === "success" ? "✓" : "!"}
                        </div>
                        <h2>{popup.title}</h2>
                        <p>{popup.message}</p>
                        <button
                            className="popup-btn"
                            onClick={() => setPopup({ ...popup, show: false })}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const ModernDetailItem = ({ icon, label, value, highlight, isBadge }) => (
    <div className={`modern-detail-item ${highlight ? `highlight-${highlight}` : ""}`}>
        <div className="detail-icon-wrap">{icon}</div>
        <div className="detail-content">
            <span className="detail-label">{label}</span>
            <span className={`detail-value ${isBadge ? "badge-value" : ""}`}>{value || "N/A"}</span>
        </div>
    </div>
);

const FormInput = ({ label, name, value, onChange, readOnly, error, isNumeric, isAlpha, maxLength }) => (
    <div className={`form-input-group ${error ? "has-error" : ""}`}>
        <label>{label}</label>
        <input
            type="text"
            value={value || ""}
            onChange={(e) => {
                if (readOnly) return;
                let val = e.target.value;
                if (isNumeric) {
                    val = val.replace(/\D/g, "");
                }

                if (isAlpha) {
                    val = val.replace(/[0-9]/g, "");
                }
                if (maxLength && val.length > maxLength) {
                    val = val.slice(0, maxLength);
                }
                onChange(prev => ({ ...prev, [name]: val }));
            }}
            placeholder={readOnly ? "" : `Enter ${label.toLowerCase()}`}
            readOnly={readOnly}
            className={`${readOnly ? "readonly-input" : ""} ${error ? "error-border" : ""}`}
        />
        {error && <span className="error-message-inline">{error}</span>}
    </div>
);

export default MasterList;
